import { useState, useEffect } from 'react';
import { Download, Wand2, Loader2, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { toast } from 'sonner';
import { readFileAsDataURL } from '@/lib/utils';
import { processAndSave, stripExt } from '@/lib/bulk';

/** Composite a transparent cutout PNG onto a solid color, return a fresh PNG blob. */
async function compositeOnColor(cutout: Blob, color: string): Promise<Blob> {
  const url = URL.createObjectURL(cutout);
  try {
    const img = new Image();
    img.src = url;
    await new Promise((res, rej) => {
      img.onload = () => res(undefined);
      img.onerror = rej;
    });
    const c = document.createElement('canvas');
    c.width = img.width;
    c.height = img.height;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0);
    return new Promise<Blob>((resolve, reject) =>
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('compose failed'))), 'image/png'),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function BackgroundRemover() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [src, setSrc] = useState('');
  const [out, setOut] = useState('');
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [bg, setBg] = useState<'transparent' | string>('transparent');

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    return () => {
      if (out) URL.revokeObjectURL(out);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);
    if (!src && next[0]) {
      setSrc(await readFileAsDataURL(next[0]));
    }
    // Output is per-file, reset when a new batch is loaded
    if (out) URL.revokeObjectURL(out);
    setOut('');
  }

  async function removeFileAt(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setSrc('');
      if (out) URL.revokeObjectURL(out);
      setOut('');
      return;
    }
    if (i === 0) {
      setSrc(await readFileAsDataURL(next[0]));
      if (out) URL.revokeObjectURL(out);
      setOut('');
    }
  }

  async function previewFirst() {
    if (!files[0]) return;
    setWorking(true);
    setProgress(0);
    setOut('');
    try {
      const { removeBackground } = await import('@imgly/background-removal');
      const blob = await removeBackground(files[0], {
        progress: (_key, current, total) => {
          if (total > 0) setProgress(Math.round((current / total) * 100));
        },
      });
      setOut((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      toast.success('Background removed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setWorking(false);
    }
  }

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setBatchProgress({ done: 0, total: files.length });
    try {
      const { removeBackground } = await import('@imgly/background-removal');
      await processAndSave(
        files,
        async (file) => {
          const cutout = await removeBackground(file);
          const blob =
            bg === 'transparent' ? cutout : await compositeOnColor(cutout, bg);
          return { blob, filename: `${stripExt(file.name)}-no-bg.png` };
        },
        {
          zipName: 'no-background',
          onProgress: (done, total) => setBatchProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setBatchProgress({ done: 0, total: 0 });
    }
  }

  const single = files.length === 1;

  return (
    <div>
      <ToolHeader
        toolId="background-remover"
        title="Background remover"
        description="ML-powered background removal — runs locally in the browser. First use downloads ~30 MB of model data. Drop multiple files for bulk."
        badges={
          <>
            {files.length > 1 && <Badge variant="accent">{files.length} files</Badge>}
            {out && <Badge variant="success">preview ready</Badge>}
          </>
        }
      />

      {files.length === 0 ? (
        <Dropzone
          accept="image/*"
          multiple
          onFiles={handleFiles}
          icon={<Wand2 size={20} />}
          hint="Photo subjects work best — people, products, animals"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">
                {single ? files[0].name : `${files.length} images queued`}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFiles([]);
                  setSrc('');
                  if (out) URL.revokeObjectURL(out);
                  setOut('');
                }}
              >
                Clear
              </Button>
            </div>

            <Button
              variant="secondary"
              className="w-full"
              onClick={previewFirst}
              loading={working && batchProgress.total === 0}
              disabled={working}
            >
              {working && batchProgress.total === 0 ? (
                <>
                  <Loader2 size={11} className="animate-spin" />
                  Working · {progress}%
                </>
              ) : (
                <>
                  <Wand2 size={11} />
                  Preview first
                </>
              )}
            </Button>
            {working && batchProgress.total === 0 && (
              <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}

            <Field label="Replace background with" className="border-t border-border pt-3">
              <div className="grid grid-cols-5 gap-1.5">
                <button
                  onClick={() => setBg('transparent')}
                  className={`h-9 rounded-md border-2 transition ${
                    bg === 'transparent' ? 'border-accent' : 'border-border'
                  }`}
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 4px 4px',
                  }}
                  title="Transparent"
                />
                {['#ffffff', '#000000', '#6366f1', '#22d3ee'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setBg(c)}
                    className={`h-9 rounded-md border-2 transition ${
                      bg === c ? 'border-accent' : 'border-border'
                    }`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              {bg !== 'transparent' && (
                <input
                  type="color"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                  className="!w-full !h-9 mt-2"
                />
              )}
            </Field>

            <Button variant="primary" className="w-full" loading={working && batchProgress.total > 0} onClick={exportAll}>
              <Download size={11} />
              {files.length > 1 ? `Process & save ${files.length}` : 'Process & save'}
            </Button>
            {working && batchProgress.total > 1 && (
              <>
                <div className="text-2xs text-fg-subtle">
                  Processing {batchProgress.done} / {batchProgress.total}…
                </div>
                <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all"
                    style={{
                      width: `${(batchProgress.done / Math.max(1, batchProgress.total)) * 100}%`,
                    }}
                  />
                </div>
              </>
            )}

            <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
              First run downloads model data (~30 MB). Subsequent runs are cached and offline. Bulk runs the model once per file.
            </div>
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">
                  Original {files.length > 1 && <span>· first of {files.length}</span>}
                </div>
                <div className="bg-bg-inset rounded p-3 flex items-center justify-center min-h-[260px]">
                  {src && <img src={src} alt="src" className="max-w-full max-h-[300px]" />}
                </div>
              </div>
              <div>
                <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">Result</div>
                <div
                  className="rounded p-3 flex items-center justify-center min-h-[260px]"
                  style={{
                    background:
                      bg === 'transparent'
                        ? 'repeating-conic-gradient(#e5e5e5 0% 25%, #ffffff 25% 50%) 50% / 16px 16px'
                        : bg,
                  }}
                >
                  {out ? (
                    <img src={out} alt="result" className="max-w-full max-h-[300px]" />
                  ) : (
                    <span className="text-xs text-fg-subtle">Hit "Preview first" to see a sample</span>
                  )}
                </div>
              </div>
            </div>

            {files.length > 1 && (
              <div className="border-t border-border mt-3 pt-3">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFileAt(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default BackgroundRemover;
